/**
 * Ambient media: silent, looping, decorative videos and Lottie animations
 * rendered by the `_macro/_media.twig` macro (video.ambient-media and
 * div.ambient-media--lottie[data-lottie]).
 *
 * Two IntersectionObservers separate loading from playback:
 *   - warm observer (rootMargin 50%): buffers video (preload="auto" + load())
 *     and mounts the Lottie poster half a viewport early, so nothing pops in
 *     blank — but nothing plays yet.
 *   - play observer (>= 5% visible): starts playback only once the element is
 *     genuinely in the viewport, restarting from frame 0 on each entry, and
 *     pauses again on exit.
 * The lottie-web player is dynamically imported the first time a Lottie mount
 * needs it, so pages without Lotties never fetch the library.
 *
 * Under prefers-reduced-motion nothing plays: videos paint their first
 * frame (preload="metadata") and Lotties render frame 0. (Deliberately
 * "first frame" rather than the motion-system's "seek to end frame" —
 * ambient loops have no settled end state; frame 0 is the poster
 * equivalent.)
 *
 * Media Accordion: hidden panels' media carry the same
 * `media-accordion-image-{n}` classes that media-accordion.js toggles
 * `is-visible` on. A suppressed (closed-panel) element is neither warmed nor
 * played, so closed panels fetch zero bytes; a MutationObserver on the class
 * attribute re-evaluates on toggle.
 */
export function ambientMedia() {
    const videos = document.querySelectorAll('video.ambient-media');
    const lotties = document.querySelectorAll('.ambient-media--lottie[data-lottie]');
    if (!videos.length && !lotties.length) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let lottieLib = null;
    const loadLottieLib = () =>
        (lottieLib ??= import('lottie-web/build/player/lottie_light').then((m) => m.default));

    const lottiePlayers = new WeakMap();
    const warmSet = new WeakSet(); // within the warm margin: buffer / mount
    const visibleSet = new WeakSet(); // >= 5% visible: actually play

    const mountLottie = (el) => {
        if (lottiePlayers.has(el)) return;
        lottiePlayers.set(el, null); // reserve while the import resolves
        loadLottieLib()
            .then((lottie) => {
                const player = lottie.loadAnimation({
                    container: el,
                    renderer: 'svg',
                    loop: true,
                    autoplay: false,
                    path: el.dataset.lottie,
                });
                // Not a Lottie file after all — remove the empty mount.
                // `data_failed` only fires on a non-200 response; `error` also
                // covers a 200 whose body parses but isn't a valid animation.
                const removeMount = () => el.remove();
                player.addEventListener('data_failed', removeMount);
                player.addEventListener('error', removeMount);
                lottiePlayers.set(el, player);
                if (prefersReducedMotion) {
                    player.goToAndStop(0, true);
                } else {
                    evaluate(el); // play now if it's already visible
                }
            })
            .catch(() => {
                // The dynamic import rejected (e.g. the chunk failed to load).
                // `??=` would otherwise keep the rejected promise cached and the
                // reserved sentinel would block a re-mount, blanking every Lottie
                // on the page until a full reload. Clear both so the next
                // intersection retries the import from scratch.
                lottiePlayers.delete(el);
                lottieLib = null;
            });
    };

    // Accordion-hosted media plays only while its panel is open
    const isSuppressed = (el) =>
        el.closest('.media-accordion-media') !== null && !el.classList.contains('is-visible');

    // Warm buffers/mounts near the viewport; play only fires once genuinely
    // visible. Restart from frame 0 on each entry, guarded on paused state so a
    // re-evaluation of an already-playing element (e.g. the warm observer
    // firing mid-scroll) doesn't reset it.
    const evaluate = (el) => {
        const suppressed = isSuppressed(el);
        const wantWarm = warmSet.has(el) && !suppressed;
        const wantPlay = visibleSet.has(el) && !suppressed;

        if (el.tagName === 'VIDEO') {
            if (wantPlay) {
                // play() drives its own buffering, so don't also call load()
                // here — a warm-triggered load() would abort playback.
                if (el.paused) {
                    el.muted = true;
                    el.currentTime = 0;
                    el.play().catch(() => {});
                }
            } else {
                el.pause();
                // Warm the buffer ahead of visibility. Setting preload alone
                // doesn't kick off a fetch; load() does. Guarded so it runs once.
                if (wantWarm && el.preload !== 'auto') {
                    el.preload = 'auto';
                    el.load();
                }
            }
        } else {
            if (wantWarm) mountLottie(el);
            const player = lottiePlayers.get(el);
            if (wantPlay) {
                if (player && player.isPaused) player.goToAndPlay(0, true);
            } else {
                player?.pause();
            }
        }
    };

    if (prefersReducedMotion) {
        // Static-frame mode: nothing plays, but stay lazy. A one-shot observer
        // does the poster work only as each element nears the viewport, so
        // below-the-fold and closed-accordion media never load metadata or
        // fetch Lottie JSON up front.
        const showStaticFrame = (el) => {
            if (el.tagName === 'VIDEO') {
                // Paint the first frame. iOS Safari won't decode a poster-less,
                // never-played video from preload="metadata" alone, so seeking a
                // hair past 0 forces it to render a frame instead of a blank box.
                el.preload = 'metadata';
                const seek = () => {
                    el.currentTime = Math.min(0.001, el.duration || 0.001);
                };
                if (el.readyState >= 1) {
                    seek();
                } else {
                    el.addEventListener('loadedmetadata', seek, { once: true });
                }
                el.load();
            } else {
                mountLottie(el); // renders frame 0 via the reduced-motion branch
            }
        };
        const staticObserver = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting) continue;
                    staticObserver.unobserve(entry.target);
                    showStaticFrame(entry.target);
                }
            },
            { rootMargin: '50% 0px 50% 0px' }
        );
        for (const el of [...videos, ...lotties]) staticObserver.observe(el);
        return;
    }

    // Two dedicated observers: playback needs re-entrant enter/exit, which the
    // one-shot motionObserver in motion.js can't express. Warm buffers/mounts
    // half a viewport early; play gates actual playback on true visibility.
    const warmObserver = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    warmSet.add(entry.target);
                } else {
                    warmSet.delete(entry.target);
                }
                evaluate(entry.target);
            }
        },
        { rootMargin: '50% 0px 50% 0px' }
    );

    const playObserver = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    visibleSet.add(entry.target);
                } else {
                    visibleSet.delete(entry.target);
                }
                evaluate(entry.target);
            }
        },
        { rootMargin: '0px', threshold: 0.05 }
    );

    for (const el of [...videos, ...lotties]) {
        warmObserver.observe(el);
        playObserver.observe(el);
        if (el.closest('.media-accordion-media')) {
            new MutationObserver(() => evaluate(el)).observe(el, {
                attributes: true,
                attributeFilter: ['class'],
            });
        }
    }
}
